"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Header } from "./Header";
import { NavBar } from "./NavBar";
import { UserDesktopRail } from "./UserDesktopRail";
import { GestorToggle } from "./GestorToggle";
import { ConfigButton } from "./ConfigButton";
import { MenuPerfil } from "./MenuPerfil";
import { NomeUsuario } from "./NomeUsuario";
import { GestorDesktopSidebar } from "./GestorDesktopSidebar";
import { ehRotaDriver } from "@/lib/biblioteca-navegacao";
import { AdminDesktopSidebar } from "./AdminDesktopSidebar";
import { ProgramadorDesktopSidebar } from "./ProgramadorDesktopSidebar";
import { PlatformAdminNavigation } from "./PlatformAdminNavigation";
import DesignModeAppearance, { DESIGN_MODE_LAYOUT_EVENT } from "./DesignModeAppearance";
import { UnsavedChangesProvider } from "./UnsavedChangesGuard";
import { WEB_APP, WEB_BETA } from "@/lib/ambiente-cliente";
import { useExperienciaProduto } from "./ExperienciaProvider";
import { FollowZapExperimentalShell } from "./FollowZapExperimentalShell";
import { ehAreaAnaliseGestorFollowZap, ehAreaGerencialFollowPro } from "@/lib/followzap-navigation";
import { deveUsarPaletaDarkFollowZap, deveUsarShellExperimentalFollowZap, useEstadoShellFollowZap } from "@/lib/followzap-shell";
import {
  buscarJsonComCache,
  invalidarCacheCliente,
  statusErroHttpCliente,
} from "@/lib/fetch-cache-cliente";

const PAPEL_AUTENTICADO_KEY = "bp-auth-papel";
const BOAS_VINDAS_EXPIRA_EM_KEY = "bp-boas-vindas-notificacao-expira-em";
const BOAS_VINDAS_CHANGE_EVENT = "bp:boas-vindas-change";
const INTERVALO_HEARTBEAT = 5 * 60_000;

function lerBoasVindasSalvas() {
  return window.sessionStorage.getItem(BOAS_VINDAS_EXPIRA_EM_KEY) ?? "";
}

function lerBoasVindasNoServidor() {
  return "";
}

function assinarBoasVindas(aoMudar: () => void) {
  window.addEventListener(BOAS_VINDAS_CHANGE_EVENT, aoMudar);
  window.addEventListener("storage", aoMudar);
  return () => {
    window.removeEventListener(BOAS_VINDAS_CHANGE_EVENT, aoMudar);
    window.removeEventListener("storage", aoMudar);
  };
}

function lerExpiracaoBoasVindas(valor: string) {
  const expiraEm = Number(valor);
  return Number.isFinite(expiraEm) && expiraEm > 0 ? expiraEm : 0;
}

function salvarExpiracaoBoasVindas(expiraEm: number | null) {
  if (expiraEm) window.sessionStorage.setItem(BOAS_VINDAS_EXPIRA_EM_KEY, String(expiraEm));
  else window.sessionStorage.removeItem(BOAS_VINDAS_EXPIRA_EM_KEY);
  window.dispatchEvent(new Event(BOAS_VINDAS_CHANGE_EVENT));
}

function normalizarPapel(papel?: string | null) {
  if (papel === "USUARIO" || papel === "GESTOR" || papel === "ADM" || papel === "PROGRAMADOR" || papel === "PLATFORM_ADMIN") return papel;
  return null;
}

function lerPapelSalvo() {
  if (typeof window === "undefined") return null;
  return normalizarPapel(window.localStorage.getItem(PAPEL_AUTENTICADO_KEY));
}

// Saudação: sem evento de assinatura; recalculada a cada render. Constante para não re-assinar.
const assinarSaudacao = () => () => {};

// Esconde o cabeçalho e a barra de navegação na tela de login.
export function Chrome({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { dados } = useExperienciaProduto();
  const estadoShellFollowZap = useEstadoShellFollowZap(WEB_BETA, dados?.produto);
  const [papelAtual, setPapelAtual] = useState<string | null>(null);
  const desktopApp = WEB_APP;
  const [sidebarCompacta, setSidebarCompacta] = useState(false);
  const [sidebarOculta, setSidebarOculta] = useState(false);
  const [designModePainelAberto, setDesignModePainelAberto] = useState(false);
  const [agora] = useState(Date.now);

  useEffect(() => {
    queueMicrotask(() => setPapelAtual(lerPapelSalvo()));
    const sincronizarPapelSalvo = () => setPapelAtual(lerPapelSalvo());
    window.addEventListener("bp-auth-role-change", sincronizarPapelSalvo);
    window.addEventListener("storage", sincronizarPapelSalvo);
    return () => {
      window.removeEventListener("bp-auth-role-change", sincronizarPapelSalvo);
      window.removeEventListener("storage", sincronizarPapelSalvo);
    };
  }, []);

  useEffect(() => {
    const sincronizarPainelDesignMode = (event: Event) => {
      const aberto = (event as CustomEvent<{ aberto?: boolean }>).detail?.aberto === true;
      setDesignModePainelAberto(aberto);
    };

    window.addEventListener(DESIGN_MODE_LAYOUT_EVENT, sincronizarPainelDesignMode);
    return () => window.removeEventListener(DESIGN_MODE_LAYOUT_EVENT, sincronizarPainelDesignMode);
  }, []);

  const semChromeEmbutido =
    typeof window !== "undefined" &&
    pathname === "/cadastro-lista" &&
    new URLSearchParams(window.location.search).get("embedded") === "1";
  const semChrome = pathname === "/login" || pathname === "/definir-senha" || pathname === "/cadastro" || pathname === "/assinar" || pathname.startsWith("/termo-avaria/") || semChromeEmbutido;
  const followProUsuario = dados?.produto === "FOLLOW_PRO" && dados.papel === "USUARIO";
  const areaGerencialAnalise = ehAreaGerencialFollowPro(pathname, dados?.produto, dados?.papel);
  const dashboardDesktopLivre = pathname === "/" && !WEB_APP && !followProUsuario && !areaGerencialAnalise;
  const rotaGestor = pathname.startsWith("/gestor");
  const gestorTermoExpandido = desktopApp && pathname === "/gestor/termo-avaria" && sidebarOculta;
  const rotaDiretorCompartilhada = pathname === "/migracao" || pathname === "/solicitacoes" || pathname === "/configuracoes";
  const rotaDiretor = pathname.startsWith("/admin") || (rotaDiretorCompartilhada && papelAtual === "ADM");
  const configuracoesDesktopSemPainelDireito = pathname === "/configuracoes" || pathname === "/gestor/configuracoes" || pathname === "/aprenda";
  const rotaProgramadorCompartilhada = pathname === "/migracao" || pathname === "/solicitacoes" || pathname === "/configuracoes";
  const rotaProgramador = pathname.startsWith("/programador") || (rotaProgramadorCompartilhada && (papelAtual === "PROGRAMADOR" || papelAtual === "PLATFORM_ADMIN"));
  const rotaAdministrativaPlataforma = (dados?.papel ?? papelAtual) === "PLATFORM_ADMIN" &&
    (pathname.startsWith("/programador") || pathname.startsWith("/admin") || rotaProgramadorCompartilhada);
  const carteiraDesktopCompacta = desktopApp && (pathname === "/carteira" || pathname === "/aprenda" || pathname === "/ferramenta");
  const etiquetasWebSemRolagem = WEB_APP && (pathname === "/profissional/etiquetas" || pathname === "/profissional/favoritos");
  const rotaUsuarioDesktop =
    !semChrome &&
    !pathname.startsWith("/gestor") &&
    !pathname.startsWith("/admin") &&
    !pathname.startsWith("/programador");
  const telaAppDesktopExpandida = desktopApp && (pathname === "/acompanhamento" || pathname === "/cadastro-novo" || pathname === "/crm" || pathname === "/ferramenta");
  const funisWebExpandido = WEB_APP && pathname === "/profissional/funis" && sidebarOculta;
  const workspaceWebComAltura = ehRotaDriver(pathname) || pathname === "/etiquetas" || pathname === "/follow-up" || pathname === "/profissional/mensagens" || pathname === "/profissional/audios" || pathname === "/profissional/midias" || pathname === "/profissional/funis" || (WEB_BETA && pathname === "/integracoes/google-planilhas");
  const usuarioDesktopComPainel = rotaUsuarioDesktop && !dashboardDesktopLivre && !areaGerencialAnalise;
  const sidebarOcultaAppUsuario = (desktopApp && sidebarOculta && (pathname === "/acompanhamento" || pathname === "/cadastro-novo" || pathname === "/crm" || pathname === "/ferramenta")) || funisWebExpandido;
  const topbarOcultaAppUsuario = sidebarOcultaAppUsuario && !funisWebExpandido;
  const shellWebIntegrado = WEB_APP && rotaUsuarioDesktop;
  const areaAnaliseGestorFollowZap = ehAreaAnaliseGestorFollowZap(pathname, dados?.produto, dados?.papel);
  const contextoVisualFollowZap = {
    produto: dados?.produto,
    papel: dados?.papel,
    desktop: estadoShellFollowZap.desktop,
    temaEscuro: estadoShellFollowZap.temaEscuro,
    areaAnaliseGestorFollowZap,
    areaGerencialAnalise,
  };
  const paletaDarkFollowZap = deveUsarPaletaDarkFollowZap(contextoVisualFollowZap);
  const shellFollowZapExperimental = deveUsarShellExperimentalFollowZap({
    ...contextoVisualFollowZap,
    preferencia: estadoShellFollowZap.preferencia,
  });
  const boasVindasSalvas = useSyncExternalStore(assinarBoasVindas, lerBoasVindasSalvas, lerBoasVindasNoServidor);
  const saudacao = useSyncExternalStore(assinarSaudacao, getSaudacao, () => "");
  const boasVindasExpiraEm = lerExpiracaoBoasVindas(boasVindasSalvas);
  const aguardandoContextoGestorAnalise = WEB_BETA && papelAtual === "GESTOR" && !dados &&
    (pathname === "/analise" || pathname.startsWith("/analise/") || pathname === "/configuracoes");
  const notificacaoBoasVindasAberta =
    !semChrome &&
    !!dados &&
    !followProUsuario &&
    !aguardandoContextoGestorAnalise &&
    !areaAnaliseGestorFollowZap &&
    !areaGerencialAnalise &&
    (papelAtual === "USUARIO" || papelAtual === "GESTOR") &&
    boasVindasExpiraEm > agora;
  const chromeWrapper = shellWebIntegrado
    ? "mx-auto min-h-dvh max-w-[430px] lg:max-w-none"
    : dashboardDesktopLivre
    ? "mx-auto min-h-dvh max-w-[430px] lg:max-w-[1680px] lg:px-8 lg:py-8"
    : rotaUsuarioDesktop
      ? "mx-auto min-h-dvh max-w-[430px] lg:max-w-[1680px] lg:px-8 lg:py-8"
    : "mx-auto min-h-dvh max-w-[430px] lg:max-w-[1500px] lg:px-6 lg:py-6";
  const reservaPainelDesignMode = designModePainelAberto ? "xl:pr-[380px]" : "";
  const designModeAppearance = semChrome ? null : <DesignModeAppearance />;

  useEffect(() => {
    document.title = dados?.marca.nome ?? "Área de trabalho";
  }, [dados?.marca.nome, pathname]);

  useEffect(() => {
    if (semChrome) return;

    let ativo = true;
    const consultarSessao = async (ttl: number, force = false) => {
      try {
        const dados = await buscarJsonComCache<{ papel?: string }>("/api/me", { ttl, force });
        const papel = normalizarPapel(dados?.papel);
        if (papel) localStorage.setItem(PAPEL_AUTENTICADO_KEY, papel);
        setPapelAtual(papel);
      } catch (erro) {
        if (statusErroHttpCliente(erro) !== 401) return;
        invalidarCacheCliente();
        localStorage.removeItem(PAPEL_AUTENTICADO_KEY);
        setPapelAtual(null);
        await fetch("/api/logout", { method: "POST" });
        if (ativo) window.location.replace("/login");
      }
    };

    let verificacaoEmCurso: Promise<void> | null = null;
    const verificarSessao = (force = true) => {
      if (verificacaoEmCurso) return verificacaoEmCurso;
      verificacaoEmCurso = consultarSessao(INTERVALO_HEARTBEAT, force).finally(() => {
        verificacaoEmCurso = null;
      });
      return verificacaoEmCurso;
    };

    const aoRetornar = () => void verificarSessao();
    const aoFicarVisivel = () => {
      if (!document.hidden) void verificarSessao();
    };

    void verificarSessao(false);
    const timer = window.setInterval(aoRetornar, INTERVALO_HEARTBEAT);
    window.addEventListener("focus", aoRetornar);
    window.addEventListener("online", aoRetornar);
    document.addEventListener("visibilitychange", aoFicarVisivel);
    return () => {
      ativo = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", aoRetornar);
      window.removeEventListener("online", aoRetornar);
      document.removeEventListener("visibilitychange", aoFicarVisivel);
    };
  }, [semChrome]);

  useEffect(() => {
    const restante = boasVindasExpiraEm - Date.now();
    if (restante <= 0) return;
    const timer = window.setTimeout(() => salvarExpiracaoBoasVindas(null), restante);
    return () => window.clearTimeout(timer);
  }, [boasVindasExpiraEm]);

  function concluirBoasVindas() {
    salvarExpiracaoBoasVindas(null);
    router.push(rotaGestor ? "/gestor/aprenda" : "/aprenda");
  }

  useEffect(() => {
    const syncSidebar = (event: Event) => {
      const detail = (event as CustomEvent<{ compacta?: boolean; oculta?: boolean }>).detail;
      setSidebarCompacta(!!detail?.compacta);
      setSidebarOculta(!!detail?.oculta);
    };

    window.addEventListener("bp:desktop-sidebar-compact", syncSidebar as EventListener);
    return () => {
      window.removeEventListener("bp:desktop-sidebar-compact", syncSidebar as EventListener);
    };
  }, []);

  const notificacaoBoasVindas = notificacaoBoasVindasAberta ? (
    <aside
      aria-live="polite"
      className="fixed bottom-20 right-4 z-[100] w-[calc(100%-2rem)] max-w-sm rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-2xl lg:bottom-6 lg:right-6"
      role="status"
    >
      <p className="text-base font-bold text-[var(--text-primary)]">Boas-vindas ao BP Acompanhamento!</p>
      <p className="mt-1 text-sm leading-5 text-[var(--text-secondary)]">
        Conheça as principais funções do sistema na tela Aprenda.
      </p>
      <button
        className="mt-3 rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        onClick={concluirBoasVindas}
        type="button"
      >
        Ir para Aprenda
      </button>
      <p className="mt-2 text-[11px] text-[var(--text-secondary)]">Esta notificação fica disponível por até 10 minutos.</p>
    </aside>
  ) : null;

  if (semChrome) {
    return <>{children}</>;
  }

  if (rotaGestor) {
    return (
      <>
      <div className={`${gestorTermoExpandido || WEB_APP ? "min-h-dvh max-w-none" : "mx-auto min-h-dvh max-w-[1800px] lg:px-8 lg:py-8"} ${desktopApp ? "bp-desktop-app" : ""} ${reservaPainelDesignMode}`}>
        {notificacaoBoasVindas}
        <div className="lg:hidden">
          <div className="relative flex min-h-dvh flex-col">
            <NavBar />
            <div className="flex flex-1 flex-col">
              <Header />
              <main className="flex-1 bg-[var(--fz-page-canvas-bg)] px-4 pb-20 pt-4">{children}</main>
            </div>
          </div>
        </div>

        {gestorTermoExpandido ? (
          <main className="hidden h-dvh min-h-0 overflow-hidden bg-[var(--fz-page-canvas-bg)] lg:block">{children}</main>
        ) : (
          <div className={`hidden lg:grid ${WEB_APP ? "lg:h-dvh lg:grid-cols-[185px_minmax(0,1fr)] lg:gap-0" : "lg:h-[calc(100dvh-4rem)] lg:grid-cols-[203px_minmax(0,1fr)] lg:gap-8"}`}>
            <GestorDesktopSidebar pathname={pathname} integrado={WEB_APP} />

            <section className={`flex min-h-0 flex-col ${WEB_APP ? "gap-0 overflow-hidden bg-[var(--surface-header)]" : "gap-6"}`}>
              <div
                data-gestor-desktop-topbar="true"
                className={`bg-[var(--surface-header)] px-7 py-3 ${WEB_APP ? "rounded-none border-b border-[var(--border)] shadow-none" : "rounded-[20px] border border-[var(--border)] shadow-[var(--shadow-soft)]"}`}
              >
                <div className="flex items-center justify-end gap-5">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="flex items-baseline gap-2.5">
                        <p className="text-[17px] font-normal leading-none text-[var(--text-secondary)]">{saudacao},</p>
                        <h1 className="text-[24px] font-semibold leading-none text-[var(--text-primary)]">
                          <NomeUsuario primeiroNome />
                        </h1>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2.5">
                    <GestorToggle />
                    {!dados ? null : (dados.produto === "FOLLOW_ZAP" || dados.produto === "FOLLOW_PRO") && dados.papel === "GESTOR"
                      ? <MenuPerfil /> : <ConfigButton />}
                  </div>
                </div>
              </div>

              <main className={`min-h-0 flex-1 overflow-y-auto bg-[var(--fz-page-canvas-bg)] ${WEB_APP ? "rounded-tl-[20px] px-8 pb-8 pt-8" : "pr-2"}`}>
                {children}
              </main>
            </section>
          </div>
        )}
      </div>
      {designModeAppearance}
      </>
    );
  }

  if (rotaAdministrativaPlataforma) {
    return (
      <>
        <div data-platform-admin-shell="true" className={`min-h-dvh max-w-none bp-desktop-app ${reservaPainelDesignMode}`}>
          <div className="relative flex min-h-dvh flex-col lg:h-dvh lg:min-h-0 lg:flex-row lg:gap-0">
            <PlatformAdminNavigation pathname={pathname} integrado />
            <section data-user-header-shell="true" className="flex min-h-0 min-w-0 flex-1 flex-col lg:overflow-hidden lg:bg-[var(--fz-header-bg)]">
              <Header variant="user-desktop" integrado administrativo />
              <main className="min-h-0 flex-1 bg-[var(--fz-page-canvas-bg)] px-4 pb-20 pt-4 lg:overflow-y-auto lg:rounded-tl-[20px] lg:px-8 lg:pb-8 lg:pt-8">
                {children}
              </main>
            </section>
          </div>
        </div>
        {designModeAppearance}
      </>
    );
  }

  if (rotaProgramador) {
    return (
      <>
      <div className={`${WEB_APP ? "min-h-dvh max-w-none" : "mx-auto min-h-dvh max-w-[1800px] lg:px-8 lg:py-8"} ${desktopApp ? "bp-desktop-app" : ""} ${reservaPainelDesignMode}`}>
        <div className="lg:hidden">
          <div className="relative flex min-h-dvh flex-col">
            <NavBar />
            <div className="flex flex-1 flex-col">
              <Header />
              <main className="flex-1 bg-[var(--fz-page-canvas-bg)] px-4 pb-20 pt-4">{children}</main>
            </div>
          </div>
        </div>

        <div className={`hidden lg:grid ${WEB_APP ? "lg:h-dvh lg:grid-cols-[185px_minmax(0,1fr)] lg:gap-0" : "lg:h-[calc(100dvh-4rem)] lg:grid-cols-[203px_minmax(0,1fr)] lg:gap-8"}`}>
          <ProgramadorDesktopSidebar pathname={pathname} integrado={WEB_APP} />

          <section className={`flex min-h-0 flex-col ${WEB_APP ? "gap-0 overflow-hidden bg-[var(--surface-header)]" : "gap-6"}`}>
            <div
              data-admin-desktop-topbar="true"
              className={`bg-[var(--surface-header)] px-7 py-3 ${WEB_APP ? "rounded-none border-b border-[var(--border)] shadow-none" : "rounded-[20px] border border-[var(--border)] shadow-[var(--shadow-soft)]"}`}
            >
              <div className="flex items-center justify-end gap-5">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-baseline gap-2.5">
                      <p className="text-[17px] font-normal leading-none text-[var(--text-secondary)]">{saudacao},</p>
                      <h1 className="text-[24px] font-semibold leading-none text-[var(--text-primary)]">
                        <NomeUsuario primeiroNome />
                      </h1>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <ConfigButton />
                </div>
              </div>
            </div>

            <main className={`min-h-0 flex-1 overflow-y-auto bg-[var(--fz-page-canvas-bg)] ${WEB_APP ? "rounded-tl-[20px] px-8 pb-8 pt-8" : "pr-2"}`}>
              {children}
            </main>
          </section>
        </div>
      </div>
      {designModeAppearance}
      </>
    );
  }

  if (rotaDiretor) {
    return (
      <>
      <div className={`${WEB_APP ? "min-h-dvh max-w-none" : "mx-auto min-h-dvh max-w-[1800px] lg:px-8 lg:py-8"} ${desktopApp ? "bp-desktop-app" : ""} ${reservaPainelDesignMode}`}>
        <div className="lg:hidden">
          <div className="relative flex min-h-dvh flex-col">
            <NavBar />
            <div className="flex flex-1 flex-col">
              <Header />
              <main className="flex-1 bg-[var(--fz-page-canvas-bg)] px-4 pb-20 pt-4">{children}</main>
            </div>
          </div>
        </div>

        <div className={`hidden lg:grid ${WEB_APP ? "lg:h-dvh lg:grid-cols-[185px_minmax(0,1fr)] lg:gap-0" : "lg:h-[calc(100dvh-4rem)] lg:grid-cols-[203px_minmax(0,1fr)] lg:gap-8"}`}>
          <AdminDesktopSidebar pathname={pathname} integrado={WEB_APP} />

          <section className={`flex min-h-0 flex-col ${WEB_APP ? "gap-0 overflow-hidden bg-[var(--surface-header)]" : "gap-6"}`}>
            <div
              data-admin-desktop-topbar="true"
              className={`bg-[var(--surface-header)] px-7 py-3 ${WEB_APP ? "rounded-none border-b border-[var(--border)] shadow-none" : "rounded-[20px] border border-[var(--border)] shadow-[var(--shadow-soft)]"}`}
            >
              <div className="flex items-center justify-end gap-5">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-baseline gap-2.5">
                      <p className="text-[17px] font-normal leading-none text-[var(--text-secondary)]">{saudacao},</p>
                      <h1 className="text-[24px] font-semibold leading-none text-[var(--text-primary)]">
                        <NomeUsuario primeiroNome />
                      </h1>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <ConfigButton />
                </div>
              </div>
            </div>

            <main className={`min-h-0 flex-1 overflow-y-auto bg-[var(--fz-page-canvas-bg)] ${WEB_APP ? "rounded-tl-[20px] px-8 pb-8 pt-8" : "pr-2"}`}>
              {children}
            </main>
          </section>
        </div>
      </div>
      {designModeAppearance}
      </>
    );
  }

  return (
    <UnsavedChangesProvider>
      <>
      <div
        className={`${chromeWrapper} ${desktopApp ? "bp-desktop-app" : ""} ${reservaPainelDesignMode}`}
        data-followzap-dark={paletaDarkFollowZap ? "true" : undefined}
        data-followzap-analise={areaAnaliseGestorFollowZap ? "true" : undefined}
        data-followpro-analise={areaGerencialAnalise ? "true" : undefined}
        data-followpro-shell={areaGerencialAnalise || followProUsuario ? "true" : undefined}
      >
        {notificacaoBoasVindas}
        <div className={`relative flex min-h-dvh flex-col lg:min-h-0 ${shellFollowZapExperimental ? "lg:h-dvh lg:flex-col lg:gap-0" : `lg:flex-row ${shellWebIntegrado ? "lg:h-dvh lg:gap-0" : rotaUsuarioDesktop ? `lg:h-[calc(100dvh-4rem)] ${sidebarOcultaAppUsuario ? "lg:gap-0" : (telaAppDesktopExpandida && sidebarCompacta) || carteiraDesktopCompacta ? "lg:gap-4" : "lg:gap-10"}` : "lg:gap-6"}`}`}>
        <div className={dashboardDesktopLivre || shellFollowZapExperimental ? "lg:hidden" : ""}>
          <NavBar />
        </div>
        <FollowZapExperimentalShell
          ativo={shellFollowZapExperimental}
          integrado={shellWebIntegrado}
          ocultarHeader={dashboardDesktopLivre || topbarOcultaAppUsuario}
          header={<Header variant={rotaUsuarioDesktop ? "user-desktop" : "default"} integrado={shellWebIntegrado} followProAnalise={areaGerencialAnalise} followProUsuario={followProUsuario} analiseGestorFollowZap={areaAnaliseGestorFollowZap} />}
          className={
            dashboardDesktopLivre
              ? "flex flex-1 flex-col lg:min-w-0"
              : `flex flex-1 flex-col lg:h-full lg:min-h-0 lg:min-w-0 lg:overflow-hidden ${shellWebIntegrado ? "lg:bg-[var(--fz-header-bg)]" : "lg:rounded-[32px] lg:border lg:border-[var(--border)] lg:bg-[var(--surface)] lg:shadow-[var(--shadow-card)] lg:backdrop-blur"}`
          }
          mainClassName={
            dashboardDesktopLivre
              ? "flex-1 bg-[var(--fz-page-canvas-bg)] px-4 pb-20 pt-4 lg:min-h-0 lg:overflow-y-auto lg:px-0 lg:pb-0 lg:pt-0"
              : `flex-1 bg-[var(--fz-page-canvas-bg)] px-4 pb-20 pt-4 lg:min-h-0 lg:px-8 lg:pt-8 ${shellWebIntegrado ? "lg:rounded-tl-[20px]" : ""} ${
                  (telaAppDesktopExpandida && sidebarCompacta) || etiquetasWebSemRolagem ? "lg:overflow-hidden lg:pb-0" : "lg:overflow-y-auto lg:pb-8"
                }`
          }
        >
            {shellWebIntegrado ? (
              <div className={workspaceWebComAltura || telaAppDesktopExpandida || etiquetasWebSemRolagem ? "min-h-0 lg:flex lg:h-full lg:flex-col" : "min-h-0"}>
                {children}
              </div>
            ) : usuarioDesktopComPainel ? (
              <>
                <div className="lg:hidden">{children}</div>
                {telaAppDesktopExpandida ? (
                  <div className="hidden lg:flex lg:h-full lg:min-h-0 lg:flex-col">
                    {children}
                  </div>
                ) : WEB_APP || carteiraDesktopCompacta || configuracoesDesktopSemPainelDireito || followProUsuario ? (
                  <div className={etiquetasWebSemRolagem ? "hidden lg:flex lg:h-full lg:min-h-0 lg:flex-col" : "hidden lg:block lg:min-h-0"}>
                    {children}
                  </div>
                ) : (
                  <div className="hidden lg:grid lg:min-h-0 lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-6">
                    <div className="min-w-0">{children}</div>
                    <UserDesktopRail />
                  </div>
                )}
              </>
            ) : (
              children
            )}
        </FollowZapExperimentalShell>
        </div>
      </div>
      {designModeAppearance}
      </>
    </UnsavedChangesProvider>
  );
}

function getSaudacao() {
  const hora = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" })).getHours();
  if (hora >= 6 && hora < 12) return "Bom dia";
  if (hora >= 12 && hora < 18) return "Boa tarde";
  return "Boa noite";
}
